/**
 * Cliente do proxy Vercel `/api/scheduler`, que assina a chamada com
 * credenciais IAM server-side e invoca a Lambda `optimus-scheduler`
 * (start/stop/status da EC2 + RDS de produção) via SDK.
 *
 * Usado em dois lugares:
 *  - painel oculto `/_/scheduler` (start/stop/status + histórico, admin);
 *  - popup de indisponibilidade da tela de login (botão "Ligar servidor").
 *
 * O `start` é público de propósito: o "poder" exposto é apenas ligar
 * recursos AWS — sem acesso a dados. Em caso de abuso, basta rotacionar
 * as credenciais IAM do proxy na Vercel.
 */

export type SchedulerAction = 'start' | 'stop' | 'status'

export interface SchedulerResourceState {
  state?: string
  ip?: string | null
  started?: boolean
  stopped?: boolean
  skipped?: boolean
  previous_state?: string
  reason?: string
}

export interface SchedulerResponse {
  action: SchedulerAction
  ec2: SchedulerResourceState
  rds: SchedulerResourceState
  error?: string
}

/**
 * Fase agregada do servidor, derivada dos estados de EC2 + RDS:
 *  - `running`  → ambos no ar (login deveria funcionar; se não, é instabilidade)
 *  - `starting` → algum recurso subindo (aguardar)
 *  - `stopped`  → ambos parados (economia noturna / fim de semana → oferecer ligar)
 *  - `mixed`    → estado intermediário/inesperado (tratado como "subindo")
 */
export type ServerPhase = 'running' | 'starting' | 'stopped' | 'mixed'

const EC2_RUNNING = new Set(['running'])
const EC2_STOPPED = new Set(['stopped'])
const EC2_STARTING = new Set(['pending'])
const RDS_RUNNING = new Set(['available'])
const RDS_STOPPED = new Set(['stopped'])
const RDS_STARTING = new Set([
  'starting',
  'creating',
  'configuring-enhanced-monitoring',
])

export function serverPhase(status: SchedulerResponse | null): ServerPhase {
  if (!status) return 'mixed'
  const ec2 = status.ec2?.state ?? ''
  const rds = status.rds?.state ?? ''

  if (EC2_RUNNING.has(ec2) && RDS_RUNNING.has(rds)) return 'running'
  if (EC2_STOPPED.has(ec2) && RDS_STOPPED.has(rds)) return 'stopped'
  if (EC2_STARTING.has(ec2) || RDS_STARTING.has(rds)) return 'starting'
  return 'mixed'
}

export async function callScheduler(
  action: SchedulerAction
): Promise<SchedulerResponse> {
  const response = await fetch(
    `/api/scheduler?action=${encodeURIComponent(action)}`,
    { method: 'GET' }
  )
  const body = (await response.json()) as SchedulerResponse
  if (!response.ok) {
    throw new Error(body.error || `HTTP ${response.status}`)
  }
  return body
}
